/**
 * Common shell for the unauthenticated + half-session auth pages
 * (Login, ChangePassword, TOTPEnroll, TOTPVerify).
 *
 * Centred card on a soft gradient backdrop, branded with the Senti
 * wordmark. ``wide`` makes the card a bit roomier for TOTP enrollment
 * (QR + recovery codes).
 */
import { type ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export function AuthShell({
  title,
  description,
  children,
  footer,
  wide = false,
}: {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  return (
    <main className="sidebar-surface relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-12">
      <BackdropGradient />
      <div className={cn("relative z-10 w-full", wide ? "max-w-xl" : "max-w-md")}>
        <div className="mb-6 flex items-center justify-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[hsl(122_48%_38%)] text-white shadow-md ring-1 ring-white/25">
            <svg
              viewBox="0 0 24 24"
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 17l6-6 4 4 6-9" />
            </svg>
          </div>
          <span className="text-lg font-semibold tracking-tight">Senti</span>
          <span className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white/80">
            Admin
          </span>
        </div>

        <Card className="text-card-foreground shadow-2xl">
          <CardHeader className="space-y-1.5 text-center">
            <CardTitle className="text-xl">{title}</CardTitle>
            {description ? (
              <p className="text-sm text-muted-foreground">{description}</p>
            ) : null}
          </CardHeader>
          <CardContent>{children}</CardContent>
        </Card>

        {footer ? (
          <div className="mt-4 text-center text-xs text-white/80 [&_a]:!text-white">{footer}</div>
        ) : null}
      </div>
    </main>
  );
}

function BackdropGradient() {
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden>
      <div className="absolute -top-32 left-1/2 h-[480px] w-[640px] -translate-x-1/2 rounded-full bg-[hsl(122_60%_45%)]/25 blur-3xl" />
      <div className="absolute -bottom-32 left-1/4 h-[360px] w-[480px] rounded-full bg-[hsl(160_80%_40%)]/20 blur-3xl" />
    </div>
  );
}
